import { useState, useEffect } from 'react';
import { Container, Table, Spinner } from 'react-bootstrap';
import { getPerformanceStatus, getMyRankHistory } from '../../api/userApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';
const th = { color: cyan, fontWeight: 600, fontSize: 13, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' };

const RANKS = [
  'STAR-10', 'STAR-20', 'STAR-30', 'STAR-40', 'STAR-50', 'STAR-60',
  'STAR-70', 'STAR-80', 'STAR-90', 'STAR-100', 'STAR-1000', 'STAR-10000',
];

const STATUS_COLOR = { active: '#66ff99', superseded: '#ffaa44', completed: '#888' };
const STATUS_LABEL = { active: 'Active', superseded: 'Superseded', completed: 'Completed' };

const fmt = fmtIST;

const PerformanceBonus = () => {
  const [performance, setPerformance] = useState([]);
  const [rankLogs, setRankLogs]       = useState([]);
  const [payoutLogs, setPayoutLogs]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');

  useEffect(() => {
    Promise.all([getPerformanceStatus(), getMyRankHistory()])
      .then(([perfRes, rankRes]) => {
        setPerformance(perfRes.data.performance);
        setRankLogs(rankRes.data.rankLogs);
        setPayoutLogs(rankRes.data.payoutLogs);
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load performance data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Container className="mt-5 text-center">
      <Spinner animation="border" style={{ color: cyan }} />
    </Container>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4">
        <h5 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Performance Bonus</h5>
        <div style={{ color: '#888', fontSize: 13, marginBottom: 8 }}>
          Hit rank milestones by growing your team in each slot.
        </div>
        <div style={{ color: '#555', fontSize: 12, marginBottom: 24 }}>
          Eligibility: Purchase the slot yourself + 10 direct referrals who have purchased the same slot.
        </div>

        {error && (
          <div style={{ color: '#ff6666', background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Rank achievement table */}
        <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(0,229,255,0.12)', marginBottom: 32 }}>
          <Table responsive className="mb-0" style={{ background: 'transparent' }}>
            <thead>
              <tr style={{ background: 'rgba(0,229,255,0.07)', borderBottom: '1px solid rgba(0,229,255,0.15)' }}>
                <th style={th}>Rank</th>
                <th style={th}>Slot</th>
                <th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {performance.map((row, idx) => (
                <tr
                  key={row.plan._id}
                  style={{
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    background: idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                    opacity: row.directCount === 0 ? 0.35 : 1,
                  }}
                >
                  <td style={{ color: '#ffaa44', fontWeight: 700, fontSize: 14, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                    {RANKS[idx] || `STAR-${row.plan.amount}`}
                  </td>
                  <td style={{ padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                    <div style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{row.plan.planName}</div>
                    <div style={{ color: '#555', fontSize: 11, marginTop: 4 }}>
                      Directs: <span style={{ color: cyan }}>{row.directCount}/10</span>
                    </div>
                  </td>
                  <td style={{ padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                    {row.achieved ? (
                      <span style={{ background: 'rgba(102,255,153,0.15)', color: '#66ff99', border: '1px solid #66ff9955', fontWeight: 500, fontSize: 12, padding: '5px 12px', borderRadius: 6 }}>
                        Achieved
                      </span>
                    ) : (
                      <span style={{ background: 'rgba(255,255,255,0.04)', color: '#555', border: '1px solid rgba(255,255,255,0.08)', fontWeight: 500, fontSize: 12, padding: '5px 12px', borderRadius: 6 }}>
                        Pending
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {performance.length === 0 && !error && (
                <tr>
                  <td colSpan={3} style={{ color: '#555', textAlign: 'center', padding: '32px', border: 'none' }}>
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* Rank History */}
        {rankLogs.length > 0 && (
          <>
            <div style={{ color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Rank History</div>
            <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(255,170,68,0.15)', marginBottom: 32 }}>
              <Table responsive className="mb-0" style={{ background: 'transparent' }}>
                <thead>
                  <tr style={{ background: 'rgba(255,170,68,0.06)', borderBottom: '1px solid rgba(255,170,68,0.15)' }}>
                    {['Rank', 'Slot', 'Achieved', 'Payouts', 'Status'].map((h) => (
                      <th key={h} style={{ ...th, color: '#ffaa44' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rankLogs.map((log, idx) => (
                    <tr
                      key={log._id}
                      style={{
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        background: idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                      }}
                    >
                      <td style={{ color: '#ffaa44', fontWeight: 700, fontSize: 14, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                        {log.rank}
                      </td>
                      <td style={{ padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                        <div style={{ color: '#fff', fontSize: 13 }}>{log.planId?.planName}</div>
                        <div style={{ color: '#66ff99', fontSize: 11, marginTop: 2 }}>{log.planId?.amount} USDT</div>
                      </td>
                      <td style={{ color: '#aaa', fontSize: 12, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                        {fmt(log.achievedAt)}
                      </td>
                      <td style={{ padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {[1, 2, 3, 4].map((n) => (
                            <div
                              key={n}
                              style={{
                                width: 20, height: 20, borderRadius: '50%',
                                background: n <= log.payoutsGiven ? '#66ff99' : 'rgba(255,255,255,0.06)',
                                border: `1px solid ${n <= log.payoutsGiven ? '#66ff99' : 'rgba(255,255,255,0.1)'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 9, color: n <= log.payoutsGiven ? '#000' : '#555', fontWeight: 700,
                              }}
                            >
                              {n}
                            </div>
                          ))}
                        </div>
                        <div style={{ color: '#555', fontSize: 10, marginTop: 4 }}>{log.payoutsGiven}/4 received</div>
                      </td>
                      <td style={{ padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                        <span style={{ color: STATUS_COLOR[log.status], fontSize: 12, fontWeight: 600 }}>
                          {STATUS_LABEL[log.status]}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}

        {/* Payout Log */}
        {payoutLogs.length > 0 && (
          <>
            <div style={{ color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Payout History</div>
            <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(102,255,153,0.12)', marginBottom: 16 }}>
              <Table responsive className="mb-0" style={{ background: 'transparent' }}>
                <thead>
                  <tr style={{ background: 'rgba(102,255,153,0.05)', borderBottom: '1px solid rgba(102,255,153,0.12)' }}>
                    {['Rank', 'Week', 'Pool', 'Holders', 'You Received', 'Payout #'].map((h) => (
                      <th key={h} style={{ ...th, color: '#66ff99' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payoutLogs.map((log, idx) => (
                    <tr
                      key={log._id}
                      style={{
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        background: idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                      }}
                    >
                      <td style={{ color: '#ffaa44', fontWeight: 700, fontSize: 13, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                        {log.rank}
                      </td>
                      <td style={{ color: '#aaa', fontSize: 12, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                        {fmt(log.periodStart)} – {fmt(log.periodEnd)}
                      </td>
                      <td style={{ color: cyan, fontSize: 12, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                        {log.poolAmount.toFixed(2)} USDT
                        <div style={{ color: '#555', fontSize: 10, marginTop: 2 }}>
                          of {log.totalSlotBusiness.toFixed(2)} business
                        </div>
                      </td>
                      <td style={{ color: '#aaa', fontSize: 13, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                        {log.holdersCount}
                      </td>
                      <td style={{ color: '#66ff99', fontWeight: 700, fontSize: 14, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                        {log.amountCredited.toFixed(2)} USDT
                      </td>
                      <td style={{ padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                        <span style={{
                          background: 'rgba(102,255,153,0.1)', color: '#66ff99',
                          border: '1px solid rgba(102,255,153,0.25)',
                          borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600,
                        }}>
                          {log.payoutNumber} / 4
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default PerformanceBonus;
